// Wire protocol types and fail-closed structural validation for the session
// family (M6 collapse-product-protocol-v1, section 2/6 spellings; the field
// spellings are registered implementation decisions pending the M6-exit ABI
// freeze — this module mirrors them exactly and must change only with the
// decision record).
//
//   client → server: collapse-session-request-v1, exactly
//     { schemaVersion, envelopeVersion, sessionId, requestId, type, payload }
//   server → client, per-session reliable stream: collapse-session-event-v1,
//     { schemaVersion, envelopeVersion, sessionId, streamSeq, type, payload }
//   server → client, connection scope: collapse-connection-event-v1,
//     { schemaVersion, envelopeVersion, sessionId, requestId, type, payload }
//
// The client validates what it consumes and fails closed: an envelope that
// is not a closed six-key object with the exact version pair, or a payload
// whose consumed fields are missing or mistyped, is a WireError — the store
// stops updating rather than render unverified bytes. The client never
// re-derives rules semantics; it projects authoritative states.

import {
  RestrictedJsonError,
  parseRestrictedJson,
  CANONICALIZATION_PROFILE,
  type RestrictedValue,
} from "./restricted-json.js";
import { rulesetMatches, type RulesetTriple } from "./identity.js";
import { PASS_ACTION_ID, type ActionKind } from "./codec.js";

export const REQUEST_SCHEMA = "collapse-session-request-v1";
export const EVENT_SCHEMA = "collapse-session-event-v1";
export const CONNECTION_SCHEMA = "collapse-connection-event-v1";
export const ENVELOPE_VERSION = 1;
export const COMMAND_SCHEMA = "collapse-command-v1";
export const ACTION_SCHEMA = "action-v1";
export const EXACT_STATE_SCHEMA = "collapse-exact-state-runtime-v1";
export const SNAPSHOT_SCHEMA = "collapse-snapshot-runtime-v1";
export const LEGAL_MASK_SCHEMA = "collapse-legal-mask-read-runtime-v1";
export const EVENT_GROUP_SCHEMA = "collapse-authoritative-event-group-runtime-v1";
export const EXPORT_FORMAT = "collapse-sgf-loss-v1";
export const ACTION_CODEC_VERSION = 1;
export const FLAT_ACTION_COUNT = 1445;
export const ANALYSIS_MAX_ROWS = 8;
export const ANALYSIS_PV_DEPTH = 8;
// The bounded ASCII identifier grammar (mirrors journal.js COMMAND_ID_RE and
// CollapseProtocol::isValidCommandId — 1..128 chars, alphanumeric first).
export const ID_GRAMMAR = /^[A-Za-z0-9][A-Za-z0-9._:/-]*$/;
export { CANONICALIZATION_PROFILE };

// Wire constants mirrored from the gateway session.js shape checks (the
// official M6 config is 19×19 only; the wire exact state is canvas-native).
export const BOARD_SIZE = 19;
export const BOARD_POINT_COUNT = 361;
export const BOARD_THRESHOLD = 150;
export const POINT_KIND_VALUES = ["NORMAL", "IMMORTAL", "DOUBLE_START", "EIGHTWAY"] as const;
export const GROUP_KINDS = [
  "ATOMIC_ONLY", "ATOMIC_WITH_EMPTY_SETTLEMENT", "ATOMIC_WITH_NONEMPTY_SETTLEMENT",
  "SCORE", "IMMEDIATE_TERMINAL",
] as const;

// The production error registry (schemas/production/collapse-error-v1.schema.json).
export const ERROR_CATEGORIES = ["INVALID_INPUT", "UNSUPPORTED", "CONFLICT", "RULE_ILLEGAL", "INTERNAL"] as const;
export const ERROR_CODES = [
  "MALFORMED_COMMAND", "UNSUPPORTED_SCHEMA", "RULESET_MISMATCH", "REVISION_CONFLICT",
  "EVENT_CURSOR_CONFLICT", "COMMAND_ID_CONFLICT", "WRONG_ACTOR", "INVALID_PHASE",
  "TERMINAL_STATE", "POINT_OFF_BOARD", "POINT_OCCUPIED", "QUOTA_EXHAUSTED",
  "DOUBLE_CONTINUATION_REQUIRED", "DOUBLE_CONTINUATION_KIND_FORBIDDEN", "DOUBLE_THRESHOLD",
  "SUICIDE", "POSITIONAL_SUPERKO", "INTERNAL_INVARIANT", "CORRUPT_STATE", "CORRUPT_SNAPSHOT",
] as const;
export const RETRY_DISPOSITIONS = ["DO_NOT_RETRY_UNCHANGED", "RESYNC_BEFORE_RETRY", "HALT_AND_ISOLATE"] as const;

export const REQUEST_TYPES = [
  "createSession", "bindSession", "submitCommand", "requestSnapshot",
  "legalMaskRead", "exportLog", "ping", "analysisStart", "analysisStop",
] as const;
export type RequestType = (typeof REQUEST_TYPES)[number];

export const EVENT_TYPES = [
  "sessionState", "sessionError", "commandResult", "snapshot",
  "legalMaskRead", "exportLog", "pong", "analysisUpdate", "analysisError",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const CONNECTION_TYPES = ["sessionBound", "transportError"] as const;
export type ConnectionType = (typeof CONNECTION_TYPES)[number];

export class WireError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WireError";
  }
}

function fail(message: string): never {
  throw new WireError(message);
}

// ---------------------------------------------------------------------------
// Restricted-value walking helpers (the ported parser yields BigInt integers
// and null-prototype objects; validators narrow to the shapes we consume).
// ---------------------------------------------------------------------------

function asObject(value: RestrictedValue, what: string): Record<string, RestrictedValue> {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    fail(`${what} must be an object`);
  return value as Record<string, RestrictedValue>;
}

function asString(value: RestrictedValue | undefined, what: string): string {
  if (typeof value !== "string") fail(`${what} must be a string`);
  return value;
}

function asBool(value: RestrictedValue | undefined, what: string): boolean {
  if (typeof value !== "boolean") fail(`${what} must be a boolean`);
  return value;
}

function asInt(value: RestrictedValue | undefined, what: string): number {
  if (typeof value !== "bigint") fail(`${what} must be an integer`);
  return Number(value); // the profile already bounded it to safe integers
}

function asNullable<T>(value: RestrictedValue | undefined, what: string, inner: (v: RestrictedValue, w: string) => T): T | null {
  if (value === null || value === undefined) {
    if (value === undefined) fail(`${what} is missing`);
    return null;
  }
  return inner(value, what);
}

function closedKeys(obj: Record<string, RestrictedValue>, expected: string[], what: string): void {
  const keys = Object.keys(obj).sort();
  const want = [...expected].sort();
  if (keys.length !== want.length || keys.some((k, i) => k !== want[i]))
    fail(`${what} must have exactly the keys {${want.join(", ")}}`);
}

// ---------------------------------------------------------------------------
// Consumed payload projections. Interfaces below carry ONLY the fields the
// client consumes; validators still check the closed key sets so a shape
// drift on the wire fails closed instead of silently misrendering.
// ---------------------------------------------------------------------------

export type Color = "BLACK" | "WHITE";
export type Player = Color | "NONE";
export type Phase = "COLLAPSE_PLAY" | "SETTLEMENT" | "ORDINARY_PLAY" | "TERMINAL";
export type PointKind = (typeof POINT_KIND_VALUES)[number];

export interface StoneSource {
  originActionNumber: number;
  originKind: PointKind;
  specialLink: number | null;
}

export interface Cell {
  color: Color | null;
  source: StoneSource | null;
}

export interface QuotaVector { IMMORTAL: number; DOUBLE_START: number; EIGHTWAY: number }
export interface PlayerQuotas { BLACK: QuotaVector; WHITE: QuotaVector }

export interface Score {
  isScored: boolean;
  blackStones: number; whiteStones: number;
  blackTerritory: number; whiteTerritory: number;
  blackScoreNumerator: number; whiteScoreNumerator: number;
  winner: Player; marginNumerator: number;
}

export interface ExactState {
  ruleset: RulesetTriple;
  boardSize: 19; // the wire exact state is always the 19×19 canvas
  cells: Cell[];
  actor: Player;
  phase: Phase;
  revision: number;
  semanticEventCursor: number;
  quotas: { initial: PlayerQuotas; remaining: PlayerQuotas; used: PlayerQuotas; expired: PlayerQuotas };
  pendingDouble: { owner: Color; specialLink: number; originActionNumber: number } | null;
  settlement: { completed: boolean };
  terminalState: { reason: "SCORE" | "RESIGNATION" | "TIMEOUT"; winner: Color; loser: Color } | null;
  score: Score;
}

export interface EventGroupSummary {
  accepted: boolean;
  groupKind: string;
  // Chain fields the store cross-checks against the tip and nextState.
  basisRevision: number;
  basisSemanticEventCursor: number;
  resultRevision: number;
  resultSemanticEventCursor: number;
  // ATOMIC_ACTION projection (display only).
  actor: Player | null;
  actionId: number | null;
  actionKind: string | null;
  capturedCount: number;
  settlementSteps: number;
  scored: boolean;
  terminalReason: string | null;
}

export const COMMAND_RESULT_SCHEMA = "collapse-command-result-runtime-v1";
export const ERROR_SCHEMA = "collapse-error-v1";

export interface CommandErrorBody {
  category: string;
  code: string;
  message: string;
  retryDisposition: string;
  authoritativeRevision: number;
  authoritativeSemanticEventCursor: number;
}

// The journaled result body (mirrors collapseprotocol.cpp): ACCEPTED carries
// the full command-result shape; REJECTED carries the bare collapse-error-v1
// object (a rejected command result never enters the journal — the error
// does), so the two branches have disjoint closed key sets.
export interface CommandResultBody {
  commandId: string;
  accepted: boolean;
  ruleset: RulesetTriple;
  exactStateSha256: string | null; // accepted only
  eventGroup: EventGroupSummary | null;
  nextState: ExactState | null;
  error: CommandErrorBody | null;
}

export interface SessionBoundPayload { revision: number; created: boolean; streamEpoch: string }
export interface TransportErrorPayload { errorClass: string }

export interface SessionEventEnvelope {
  sessionId: string;
  streamSeq: number;
  type: EventType;
  payload: RestrictedValue;
}

export interface ConnectionEventEnvelope {
  sessionId: string | null;
  requestId: string | null;
  type: ConnectionType;
  payload: RestrictedValue;
}

export type InboundEnvelope =
  | { kind: "sessionEvent"; event: SessionEventEnvelope }
  | { kind: "connectionEvent"; event: ConnectionEventEnvelope };

// ---------------------------------------------------------------------------
// Payload validators for the consumed projections.
// ---------------------------------------------------------------------------

function validateColor(value: RestrictedValue, what: string): Color {
  const s = asString(value, what);
  if (s !== "BLACK" && s !== "WHITE") fail(`${what} must be BLACK or WHITE`);
  return s;
}

function validatePlayer(value: RestrictedValue, what: string): Player {
  const s = asString(value, what);
  if (s !== "BLACK" && s !== "WHITE" && s !== "NONE") fail(`${what} must be BLACK, WHITE or NONE`);
  return s;
}

function validateRuleset(value: RestrictedValue | undefined, what: string): RulesetTriple {
  if (value === undefined) fail(`${what} is missing`);
  const obj = asObject(value, what);
  closedKeys(obj, ["rulesetId", "semanticVersion", "descriptorSha256"], what);
  if (!rulesetMatches(value))
    fail(`${what} does not match the frozen public identity triple`);
  return value as unknown as RulesetTriple;
}

function validateQuotaVector(value: RestrictedValue, what: string): QuotaVector {
  const obj = asObject(value, what);
  closedKeys(obj, ["IMMORTAL", "DOUBLE_START", "EIGHTWAY"], what);
  return {
    IMMORTAL: asNonNegativeInt(obj["IMMORTAL"], `${what}.IMMORTAL`),
    DOUBLE_START: asNonNegativeInt(obj["DOUBLE_START"], `${what}.DOUBLE_START`),
    EIGHTWAY: asNonNegativeInt(obj["EIGHTWAY"], `${what}.EIGHTWAY`),
  };
}

function validatePlayerQuotas(value: RestrictedValue, what: string): PlayerQuotas {
  const obj = asObject(value, what);
  closedKeys(obj, ["BLACK", "WHITE"], what);
  return {
    BLACK: validateQuotaVector(obj["BLACK"]!, `${what}.BLACK`),
    WHITE: validateQuotaVector(obj["WHITE"]!, `${what}.WHITE`),
  };
}

function validateScore(value: RestrictedValue, what: string): Score {
  const obj = asObject(value, what);
  closedKeys(obj, [
    "isScored", "blackStones", "whiteStones", "blackTerritory", "whiteTerritory",
    "blackScoreNumerator", "whiteScoreNumerator", "winner", "marginNumerator",
  ], what);
  return {
    isScored: asBool(obj["isScored"], `${what}.isScored`),
    blackStones: asNonNegativeInt(obj["blackStones"], `${what}.blackStones`),
    whiteStones: asNonNegativeInt(obj["whiteStones"], `${what}.whiteStones`),
    blackTerritory: asNonNegativeInt(obj["blackTerritory"], `${what}.blackTerritory`),
    whiteTerritory: asNonNegativeInt(obj["whiteTerritory"], `${what}.whiteTerritory`),
    blackScoreNumerator: asNonNegativeInt(obj["blackScoreNumerator"], `${what}.blackScoreNumerator`),
    whiteScoreNumerator: asNonNegativeInt(obj["whiteScoreNumerator"], `${what}.whiteScoreNumerator`),
    winner: validatePlayer(obj["winner"]!, `${what}.winner`),
    marginNumerator: asNonNegativeInt(obj["marginNumerator"], `${what}.marginNumerator`),
  };
}

// --- exact-state subtree helpers (mirror session.js requireExactStateShape) -

function asNonNegativeInt(value: RestrictedValue | undefined, what: string): number {
  const n = asInt(value, what);
  if (n < 0) fail(`${what} must be non-negative`);
  return n;
}

function asPositiveInt(value: RestrictedValue | undefined, what: string): number {
  const n = asInt(value, what);
  if (n < 1) fail(`${what} must be positive`);
  return n;
}

function requireBoardPoint(value: RestrictedValue | undefined, what: string): number {
  const n = asInt(value, what);
  if (n < 0 || n > BOARD_POINT_COUNT - 1) fail(`${what} is not a board point in 0..360`);
  return n;
}

function requireEnum<T extends string>(value: RestrictedValue | undefined, allowed: readonly T[], what: string): T {
  const s = asString(value, what);
  if (!allowed.includes(s as T)) fail(`${what} must be one of ${allowed.join(", ")}`);
  return s as T;
}

function validateStoneSource(value: RestrictedValue, what: string): StoneSource {
  const obj = asObject(value, what);
  closedKeys(obj, ["originActionNumber", "originKind", "specialLink"], what);
  const originActionNumber = asPositiveInt(obj["originActionNumber"], `${what}.originActionNumber`);
  const originKind = requireEnum(obj["originKind"], POINT_KIND_VALUES, `${what}.originKind`);
  const specialLinkRaw = obj["specialLink"];
  const specialLink = specialLinkRaw === null ? null : asPositiveInt(specialLinkRaw, `${what}.specialLink`);
  // CollapseGoStoneSource::checkConsistency: NORMAL carries no link; special
  // kinds link to their own origin action number.
  if (originKind === "NORMAL") {
    if (specialLink !== null) fail(`${what}: a NORMAL source cannot carry a special link`);
  } else if (specialLink !== originActionNumber) {
    fail(`${what}: the special link must equal the origin action number`);
  }
  return { originActionNumber, originKind, specialLink };
}

// stableOccupancy / position occupancy: closed {black, white} point lists.
function validateOccupancy(value: RestrictedValue, what: string): number {
  // Returns the total point count (used for the captured-count projection).
  const obj = asObject(value, what);
  closedKeys(obj, ["black", "white"], what);
  let total = 0;
  for (const side of ["black", "white"] as const) {
    const list = obj[side];
    if (!Array.isArray(list)) fail(`${what}.${side} must be an array`);
    // pointList: at most 361 unique board points (a duplicate captured point
    // would double-count in the UI projection).
    if (list.length > BOARD_POINT_COUNT) fail(`${what}.${side} exceeds the 361-entry cap`);
    const seen = new Set<number>();
    for (let i = 0; i < list.length; i++) {
      const point = requireBoardPoint(list[i], `${what}.${side}[${i}]`);
      if (seen.has(point)) fail(`${what}.${side} carries duplicate point ${point}`);
      seen.add(point);
    }
    total += list.length;
  }
  return total;
}

function validateBoard(value: RestrictedValue, what: string): Cell[] {
  const board = asObject(value, what);
  closedKeys(board, ["boardSize", "cells"], what);
  if (asInt(board["boardSize"], `${what}.boardSize`) !== BOARD_SIZE)
    fail(`${what}.boardSize is not 19`);
  const cellsValue = board["cells"];
  if (!Array.isArray(cellsValue)) fail(`${what}.cells must be an array`);
  if (cellsValue.length !== BOARD_POINT_COUNT)
    fail(`${what}.cells must have exactly 361 entries`);
  return cellsValue.map((cell, i) => {
    const c = asObject(cell, `${what}.cells[${i}]`);
    closedKeys(c, ["color", "source"], `${what}.cells[${i}]`);
    const colorRaw = c["color"];
    if (colorRaw === null) {
      if (c["source"] !== null)
        fail(`${what}.cells[${i}]: an empty cell carries a stone source`);
      return { color: null, source: null };
    }
    const color = validateColor(colorRaw!, `${what}.cells[${i}].color`);
    const sourceRaw = c["source"];
    if (sourceRaw === null || sourceRaw === undefined)
      fail(`${what}.cells[${i}]: an occupied cell lacks a stone source`);
    return { color, source: validateStoneSource(sourceRaw, `${what}.cells[${i}].source`) };
  });
}

export function validateExactState(value: RestrictedValue, what: string): ExactState {
  const obj = asObject(value, what);
  closedKeys(obj, [
    "schemaVersion", "canonicalizationProfile", "ruleset", "board", "actor", "phase",
    "counters", "revision", "semanticEventCursor", "quotas", "ledger", "pendingDouble",
    "settlement", "atomicHistory", "liveSourceCommitments", "pskHistory",
    "terminalState", "score",
  ], what);
  if (asString(obj["schemaVersion"], `${what}.schemaVersion`) !== EXACT_STATE_SCHEMA)
    fail(`${what}.schemaVersion is not collapse-exact-state-runtime-v1`);
  requireProfile(obj["canonicalizationProfile"], `${what}.canonicalizationProfile`);
  const ruleset = validateRuleset(obj["ruleset"], `${what}.ruleset`);

  const cells = validateBoard(obj["board"]!, `${what}.board`);

  const counters = asObject(obj["counters"]!, `${what}.counters`);
  closedKeys(counters, [
    "atomicActionCount", "consecutivePasses", "settledLedgerCount", "stableTerminalEventCount", "threshold",
  ], `${what}.counters`);
  if (asInt(counters["threshold"], `${what}.counters.threshold`) !== BOARD_THRESHOLD)
    fail(`${what}.counters.threshold is not the 19x19 constant 150`);
  asNonNegativeInt(counters["atomicActionCount"], `${what}.counters.atomicActionCount`);
  asNonNegativeInt(counters["consecutivePasses"], `${what}.counters.consecutivePasses`);
  asNonNegativeInt(counters["settledLedgerCount"], `${what}.counters.settledLedgerCount`);
  asNonNegativeInt(counters["stableTerminalEventCount"], `${what}.counters.stableTerminalEventCount`);

  const phase = requireEnum(obj["phase"], ["COLLAPSE_PLAY", "SETTLEMENT", "ORDINARY_PLAY", "TERMINAL"] as const, `${what}.phase`);

  const quotasObj = asObject(obj["quotas"]!, `${what}.quotas`);
  closedKeys(quotasObj, ["initial", "remaining", "used", "expired"], `${what}.quotas`);

  const ledger = obj["ledger"];
  if (!Array.isArray(ledger) || ledger.length > BOARD_POINT_COUNT)
    fail(`${what}.ledger is not an array within the 361-entry cap`);
  for (let i = 0; i < ledger.length; i++) {
    const entry = asObject(ledger[i]!, `${what}.ledger[${i}]`);
    closedKeys(entry, [
      "abilityState", "originActionNumber", "originKind", "owner",
      "settlementState", "sourcePoint", "specialLink", "stoneState", "tombstone",
    ], `${what}.ledger[${i}]`);
    const specialLink = asPositiveInt(entry["specialLink"], `${what}.ledger[${i}].specialLink`);
    const originActionNumber = asPositiveInt(entry["originActionNumber"], `${what}.ledger[${i}].originActionNumber`);
    // CollapseGoLedgerEntry constructor: the link equals the origin number.
    if (specialLink !== originActionNumber)
      fail(`${what}.ledger[${i}]: the link must equal the origin action number`);
    requireEnum(entry["owner"], ["BLACK", "WHITE"] as const, `${what}.ledger[${i}].owner`);
    requireEnum(entry["originKind"], ["IMMORTAL", "DOUBLE_START", "EIGHTWAY"] as const, `${what}.ledger[${i}].originKind`);
    requireBoardPoint(entry["sourcePoint"], `${what}.ledger[${i}].sourcePoint`);
    requireEnum(entry["abilityState"], ["ARMED", "CONSUMED", "INACTIVE"] as const, `${what}.ledger[${i}].abilityState`);
    requireEnum(entry["stoneState"], ["ON_BOARD", "CAPTURED"] as const, `${what}.ledger[${i}].stoneState`);
    requireEnum(entry["settlementState"], ["PENDING", "SETTLED"] as const, `${what}.ledger[${i}].settlementState`);
    asBool(entry["tombstone"], `${what}.ledger[${i}].tombstone`);
  }

  const pendingRaw = obj["pendingDouble"];
  const pendingDouble = pendingRaw === null ? null : (() => {
    const pd = asObject(pendingRaw!, `${what}.pendingDouble`);
    closedKeys(pd, ["owner", "specialLink", "originActionNumber"], `${what}.pendingDouble`);
    const specialLink = asPositiveInt(pd["specialLink"], `${what}.pendingDouble.specialLink`);
    const originActionNumber = asPositiveInt(pd["originActionNumber"], `${what}.pendingDouble.originActionNumber`);
    // CollapseGoPendingDouble constructor: the link equals the origin number.
    if (specialLink !== originActionNumber)
      fail(`${what}.pendingDouble: the link must equal the origin action number`);
    return {
      owner: validateColor(pd["owner"]!, `${what}.pendingDouble.owner`),
      specialLink,
      originActionNumber,
    };
  })();
  if (pendingRaw === undefined) fail(`${what}.pendingDouble is missing`);

  const settlementObj = asObject(obj["settlement"]!, `${what}.settlement`);
  closedKeys(settlementObj, ["completed", "provenance"], `${what}.settlement`);
  const settlementCompleted = asBool(settlementObj["completed"], `${what}.settlement.completed`);
  const provenance = asObject(settlementObj["provenance"]!, `${what}.settlement.provenance`);
  closedKeys(provenance, [
    "handoffActor", "kind", "ledgerEntryCount", "reason", "triggerActionNumber",
  ], `${what}.settlement.provenance`);
  const provKind = requireEnum(provenance["kind"], ["NONE", "EMPTY_LEDGER", "LEDGER_EVENTS"] as const, `${what}.settlement.provenance.kind`);
  const provReason = requireEnum(provenance["reason"], ["NONE", "THRESHOLD", "PRE_THRESHOLD_TWO_PASSES"] as const, `${what}.settlement.provenance.reason`);
  const provTrigger = asNonNegativeInt(provenance["triggerActionNumber"], `${what}.settlement.provenance.triggerActionNumber`);
  const provHandoff = requireEnum(provenance["handoffActor"], ["BLACK", "WHITE", "NONE"] as const, `${what}.settlement.provenance.handoffActor`);
  const provLedgerCount = asNonNegativeInt(provenance["ledgerEntryCount"], `${what}.settlement.provenance.ledgerEntryCount`);
  // CollapseGoSettlementProvenance constructors: NONE requires the zero
  // fields; a committed provenance requires a real reason, a positive
  // trigger, a BLACK/WHITE handoff, and EMPTY_LEDGER <=> count == 0.
  if (provKind === "NONE") {
    if (provReason !== "NONE" || provTrigger !== 0 || provHandoff !== "NONE" || provLedgerCount !== 0)
      fail(`${what}.settlement.provenance: NONE requires the zero fields`);
  } else {
    if (provReason === "NONE" || provTrigger < 1 || provHandoff === "NONE")
      fail(`${what}.settlement.provenance: a committed provenance lacks a real reason, trigger, or handoff`);
    if ((provKind === "EMPTY_LEDGER") !== (provLedgerCount === 0))
      fail(`${what}.settlement.provenance: the ledger count disagrees with the kind`);
  }

  const atomicHistory = obj["atomicHistory"];
  if (!Array.isArray(atomicHistory) || atomicHistory.length > 5)
    fail(`${what}.atomicHistory is not an array within the 5-entry cap`);
  for (let i = 0; i < atomicHistory.length; i++) {
    const entry = asObject(atomicHistory[i]!, `${what}.atomicHistory[${i}]`);
    closedKeys(entry, ["action", "actionNumber", "actor", "finalPosition"], `${what}.atomicHistory[${i}]`);
    requireEnum(entry["actor"], ["BLACK", "WHITE"] as const, `${what}.atomicHistory[${i}].actor`);
    validateActionV1(entry["action"]!, `${what}.atomicHistory[${i}].action`);
    asPositiveInt(entry["actionNumber"], `${what}.atomicHistory[${i}].actionNumber`);
    validateBoard(entry["finalPosition"]!, `${what}.atomicHistory[${i}].finalPosition`);
  }

  const commitments = obj["liveSourceCommitments"];
  if (!Array.isArray(commitments) || commitments.length > BOARD_POINT_COUNT)
    fail(`${what}.liveSourceCommitments is not an array within the 361-entry cap`);
  for (let i = 0; i < commitments.length; i++) {
    const commitment = asObject(commitments[i]!, `${what}.liveSourceCommitments[${i}]`);
    closedKeys(commitment, ["color", "point", "source"], `${what}.liveSourceCommitments[${i}]`);
    requireBoardPoint(commitment["point"], `${what}.liveSourceCommitments[${i}].point`);
    requireEnum(commitment["color"], ["BLACK", "WHITE"] as const, `${what}.liveSourceCommitments[${i}].color`);
    validateStoneSource(commitment["source"]!, `${what}.liveSourceCommitments[${i}].source`);
  }

  const pskHistory = obj["pskHistory"];
  if (!Array.isArray(pskHistory) || pskHistory.length === 0 || pskHistory.length > 100000)
    fail(`${what}.pskHistory is not within the 1..100000 entry bounds`);
  for (let i = 0; i < pskHistory.length; i++) {
    const entry = asObject(pskHistory[i]!, `${what}.pskHistory[${i}]`);
    closedKeys(entry, ["index", "occupancy"], `${what}.pskHistory[${i}]`);
    if (asInt(entry["index"], `${what}.pskHistory[${i}].index`) !== i)
      fail(`${what}.pskHistory[${i}].index does not match its position`);
    const occupancy = entry["occupancy"];
    if (!Array.isArray(occupancy) || occupancy.length !== BOARD_POINT_COUNT)
      fail(`${what}.pskHistory[${i}].occupancy is not exactly 361 cells`);
    for (let j = 0; j < occupancy.length; j++) {
      const cell = asInt(occupancy[j]!, `${what}.pskHistory[${i}].occupancy[${j}]`);
      if (cell < 0 || cell > 2)
        fail(`${what}.pskHistory[${i}].occupancy[${j}] is outside 0..2`);
    }
  }

  const terminalRaw = obj["terminalState"];
  const terminalState = terminalRaw === null ? null : (() => {
    const ts = asObject(terminalRaw!, `${what}.terminalState`);
    closedKeys(ts, ["reason", "winner", "loser"], `${what}.terminalState`);
    const reason = requireEnum(ts["reason"], ["SCORE", "RESIGNATION", "TIMEOUT"] as const, `${what}.terminalState.reason`);
    // CollapseGoTerminalState constructor: winner is BLACK/WHITE and the
    // loser is the winner's opponent.
    const winner = validateColor(ts["winner"]!, `${what}.terminalState.winner`);
    const loser = validateColor(ts["loser"]!, `${what}.terminalState.loser`);
    if (loser === winner) fail(`${what}.terminalState: the loser must be the winner's opponent`);
    return { reason, winner, loser };
  })();
  if (terminalRaw === undefined) fail(`${what}.terminalState is missing`);

  return {
    ruleset,
    boardSize: BOARD_SIZE,
    cells,
    actor: validatePlayer(obj["actor"]!, `${what}.actor`),
    phase,
    revision: asNonNegativeInt(obj["revision"], `${what}.revision`),
    semanticEventCursor: asNonNegativeInt(obj["semanticEventCursor"], `${what}.semanticEventCursor`),
    quotas: {
      initial: validatePlayerQuotas(quotasObj["initial"]!, `${what}.quotas.initial`),
      remaining: validatePlayerQuotas(quotasObj["remaining"]!, `${what}.quotas.remaining`),
      used: validatePlayerQuotas(quotasObj["used"]!, `${what}.quotas.used`),
      expired: validatePlayerQuotas(quotasObj["expired"]!, `${what}.quotas.expired`),
    },
    pendingDouble,
    settlement: { completed: settlementCompleted },
    terminalState,
    score: validateScore(obj["score"]!, `${what}.score`),
  };
}

function validateTerminalEventShape(value: RestrictedValue, what: string, scored: boolean): string {
  // terminalEventJson: SCORE events carry score; IMMEDIATE_TERMINAL carries
  // reason. Returns the terminal reason ("SCORE" included).
  const obj = asObject(value, what);
  closedKeys(obj,
    scored
      ? ["eventType", "eventSequence", "pskHistoryIndex", "winner", "loser", "score", "stableOccupancy"]
      : ["eventType", "eventSequence", "pskHistoryIndex", "reason", "winner", "loser", "stableOccupancy"],
    what);
  if (asString(obj["eventType"], `${what}.eventType`) !== (scored ? "SCORE" : "IMMEDIATE_TERMINAL"))
    fail(`${what}.eventType must be ${scored ? "SCORE" : "IMMEDIATE_TERMINAL"}`);
  asPositiveInt(obj["eventSequence"], `${what}.eventSequence`);
  asPositiveInt(obj["pskHistoryIndex"], `${what}.pskHistoryIndex`);
  // terminalEventJson winners/losers are always BLACK/WHITE.
  validateColor(obj["winner"]!, `${what}.winner`);
  validateColor(obj["loser"]!, `${what}.loser`);
  if (scored) {
    const score = asObject(obj["score"]!, `${what}.score`);
    closedKeys(score, [
      "method", "blackStones", "whiteStones", "blackEmptyArea", "whiteEmptyArea",
      "blackScoreNumerator", "whiteScoreNumerator", "marginNumerator", "denominator",
    ], `${what}.score`);
    if (asString(score["method"], `${what}.score.method`) !== "CHINESE_AREA")
      fail(`${what}.score.method must be CHINESE_AREA`);
    if (asInt(score["denominator"], `${what}.score.denominator`) !== 2)
      fail(`${what}.score.denominator must be 2`);
    for (const field of ["blackStones", "whiteStones", "blackEmptyArea", "whiteEmptyArea",
      "blackScoreNumerator", "whiteScoreNumerator", "marginNumerator"] as const)
      asNonNegativeInt(score[field], `${what}.score.${field}`);
  } else {
    requireEnum(obj["reason"], ["RESIGNATION", "TIMEOUT"] as const, `${what}.reason`);
  }
  validateOccupancy(obj["stableOccupancy"]!, `${what}.stableOccupancy`);
  return scored ? "SCORE" : asString(obj["reason"], `${what}.reason`);
}

function validateEventGroup(value: RestrictedValue, what: string): EventGroupSummary {
  const obj = asObject(value, what);
  closedKeys(obj, [
    "schemaVersion", "canonicalizationProfile", "commandId", "ruleset", "boardSize",
    "basisRevision", "basisSemanticEventCursor", "resultRevision",
    "resultSemanticEventCursor", "resultExactStateSha256", "accepted", "groupKind",
    "atomicEvent", "settlement", "scoreEvent", "terminalEvent", "pskAppendRange",
  ], what);
  if (asString(obj["schemaVersion"], `${what}.schemaVersion`) !== EVENT_GROUP_SCHEMA)
    fail(`${what}.schemaVersion is not collapse-authoritative-event-group-runtime-v1`);
  requireProfile(obj["canonicalizationProfile"], `${what}.canonicalizationProfile`);
  validateRuleset(obj["ruleset"], `${what}.ruleset`);
  if (asInt(obj["boardSize"], `${what}.boardSize`) !== BOARD_SIZE)
    fail(`${what}.boardSize is not 19`);
  if (asBool(obj["accepted"], `${what}.accepted`) !== true)
    fail(`${what}.accepted must be true on a published event group`);
  const groupKind = requireEnum(obj["groupKind"], GROUP_KINDS, `${what}.groupKind`);

  const atomicRaw = obj["atomicEvent"];
  let actor: Player | null = null;
  let actionId: number | null = null;
  let actionKind: string | null = null;
  let capturedCount = 0;
  if (atomicRaw !== null) {
    if (atomicRaw === undefined) fail(`${what}.atomicEvent is missing`);
    const atomic = asObject(atomicRaw, `${what}.atomicEvent`);
    closedKeys(atomic, [
      "eventType", "eventSequence", "pskHistoryIndex", "atomicActionNumber",
      "actor", "action", "captured", "stableOccupancy",
    ], `${what}.atomicEvent`);
    if (asString(atomic["eventType"], `${what}.atomicEvent.eventType`) !== "ATOMIC_ACTION")
      fail(`${what}.atomicEvent.eventType must be ATOMIC_ACTION`);
    asPositiveInt(atomic["eventSequence"], `${what}.atomicEvent.eventSequence`);
    asPositiveInt(atomic["pskHistoryIndex"], `${what}.atomicEvent.pskHistoryIndex`);
    asPositiveInt(atomic["atomicActionNumber"], `${what}.atomicEvent.atomicActionNumber`);
    actor = validateColor(atomic["actor"]!, `${what}.atomicEvent.actor`);
    const action = validateActionV1(atomic["action"]!, `${what}.atomicEvent.action`);
    actionId = action.actionId;
    actionKind = action.kind;
    // captured is an occupancy $ref: the same pointList caps/dedup apply.
    capturedCount = validateOccupancy(atomic["captured"]!, `${what}.atomicEvent.captured`);
    validateOccupancy(atomic["stableOccupancy"]!, `${what}.atomicEvent.stableOccupancy`);
  }

  let settlementSteps = 0;
  let settlementLedgerCount = 0;
  const settlementRaw = obj["settlement"];
  if (settlementRaw !== null && settlementRaw !== undefined) {
    const settlement = asObject(settlementRaw, `${what}.settlement`);
    closedKeys(settlement, [
      "triggerReason", "triggeringAtomicActionNumber", "entryActor", "entryPhase",
      "handoffActor", "ledgerEntryCount", "remainingQuotasExpired",
      "consecutivePassesResetToZero", "exitPhase", "steps",
    ], `${what}.settlement`);
    requireEnum(settlement["triggerReason"], ["THRESHOLD", "PRE_THRESHOLD_TWO_PASSES"] as const, `${what}.settlement.triggerReason`);
    asPositiveInt(settlement["triggeringAtomicActionNumber"], `${what}.settlement.triggeringAtomicActionNumber`);
    validateColor(settlement["entryActor"]!, `${what}.settlement.entryActor`);
    if (asString(settlement["entryPhase"], `${what}.settlement.entryPhase`) !== "COLLAPSE_PLAY")
      fail(`${what}.settlement.entryPhase must be COLLAPSE_PLAY`);
    validateColor(settlement["handoffActor"]!, `${what}.settlement.handoffActor`);
    settlementLedgerCount = asNonNegativeInt(settlement["ledgerEntryCount"], `${what}.settlement.ledgerEntryCount`);
    if (asBool(settlement["remainingQuotasExpired"], `${what}.settlement.remainingQuotasExpired`) !== true)
      fail(`${what}.settlement.remainingQuotasExpired must be true`);
    if (asBool(settlement["consecutivePassesResetToZero"], `${what}.settlement.consecutivePassesResetToZero`) !== true)
      fail(`${what}.settlement.consecutivePassesResetToZero must be true`);
    if (asString(settlement["exitPhase"], `${what}.settlement.exitPhase`) !== "ORDINARY_PLAY")
      fail(`${what}.settlement.exitPhase must be ORDINARY_PLAY`);
    const steps = settlement["steps"];
    if (!Array.isArray(steps)) fail(`${what}.settlement.steps must be an array`);
    if (steps.length > 2166) fail(`${what}.settlement.steps exceeds the 2166-entry cap`);
    settlementSteps = steps.length;
    for (let i = 0; i < steps.length; i++) {
      const step = asObject(steps[i]!, `${what}.settlement.steps[${i}]`);
      closedKeys(step, [
        "eventType", "eventSequence", "pskHistoryIndex", "stepIndex", "specialLink",
        "originActionNumber", "owner", "originKind", "sourcePoint", "abilityDeactivated",
        "noOp", "removalBatches", "stableOccupancy",
      ], `${what}.settlement.steps[${i}]`);
      if (asString(step["eventType"], `${what}.settlement.steps[${i}].eventType`) !== "SETTLEMENT_STEP")
        fail(`${what}.settlement.steps[${i}].eventType must be SETTLEMENT_STEP`);
      asPositiveInt(step["eventSequence"], `${what}.settlement.steps[${i}].eventSequence`);
      asPositiveInt(step["pskHistoryIndex"], `${what}.settlement.steps[${i}].pskHistoryIndex`);
      asNonNegativeInt(step["stepIndex"], `${what}.settlement.steps[${i}].stepIndex`);
      asPositiveInt(step["specialLink"], `${what}.settlement.steps[${i}].specialLink`);
      asPositiveInt(step["originActionNumber"], `${what}.settlement.steps[${i}].originActionNumber`);
      validateColor(step["owner"]!, `${what}.settlement.steps[${i}].owner`);
      requireEnum(step["originKind"], ["IMMORTAL", "DOUBLE_START", "EIGHTWAY"] as const, `${what}.settlement.steps[${i}].originKind`);
      requireBoardPoint(step["sourcePoint"], `${what}.settlement.steps[${i}].sourcePoint`);
      asBool(step["abilityDeactivated"], `${what}.settlement.steps[${i}].abilityDeactivated`);
      asBool(step["noOp"], `${what}.settlement.steps[${i}].noOp`);
      const batches = step["removalBatches"];
      if (!Array.isArray(batches)) fail(`${what}.settlement.steps[${i}].removalBatches must be an array`);
      if (batches.length > BOARD_POINT_COUNT)
        fail(`${what}.settlement.steps[${i}].removalBatches exceeds the 361-entry cap`);
      for (let j = 0; j < batches.length; j++) validateOccupancy(batches[j]!, `${what}.settlement.steps[${i}].removalBatches[${j}]`);
      validateOccupancy(step["stableOccupancy"]!, `${what}.settlement.steps[${i}].stableOccupancy`);
    }
  }

  const scoreRaw = obj["scoreEvent"];
  const terminalRaw = obj["terminalEvent"];
  if (scoreRaw === undefined || terminalRaw === undefined)
    fail(`${what} is missing scoreEvent or terminalEvent`);
  let scored = false;
  let terminalReason: string | null = null;
  if (scoreRaw !== null) {
    terminalReason = validateTerminalEventShape(scoreRaw, `${what}.scoreEvent`, true);
    scored = true;
  }
  if (terminalRaw !== null)
    terminalReason = validateTerminalEventShape(terminalRaw, `${what}.terminalEvent`, false);

  // groupKind/content consistency (eventGroupJson):
  //   terminal closures never share a group with a settlement; nonterminal
  //   groups always carry their atomic event.
  if (groupKind === "ATOMIC_ONLY") {
    if (atomicRaw === null || settlementRaw !== null || scoreRaw !== null || terminalRaw !== null)
      fail(`${what}: ATOMIC_ONLY requires exactly the atomic event`);
  } else if (groupKind === "ATOMIC_WITH_EMPTY_SETTLEMENT" || groupKind === "ATOMIC_WITH_NONEMPTY_SETTLEMENT") {
    if (atomicRaw === null || settlementRaw === null || scoreRaw !== null || terminalRaw !== null)
      fail(`${what}: ${groupKind} requires the atomic event and a settlement, nothing else`);
    if ((groupKind === "ATOMIC_WITH_EMPTY_SETTLEMENT") !== (settlementSteps === 0))
      fail(`${what}: the settlement step count disagrees with the groupKind`);
    if ((groupKind === "ATOMIC_WITH_EMPTY_SETTLEMENT") !== (settlementLedgerCount === 0))
      fail(`${what}: the settlement ledgerEntryCount disagrees with the groupKind`);
  } else if (groupKind === "SCORE") {
    // Schema allOf: the scoring pass group still carries its atomic event.
    if (scoreRaw === null || terminalRaw !== null || settlementRaw !== null || atomicRaw === null)
      fail(`${what}: SCORE requires the atomic and score events, no terminal event, no settlement`);
  } else {
    // IMMEDIATE_TERMINAL (administrative: no atomic event, never settles)
    if (terminalRaw === null || scoreRaw !== null || settlementRaw !== null || atomicRaw !== null)
      fail(`${what}: IMMEDIATE_TERMINAL requires exactly the terminal event`);
  }

  // An accepted transition always appends at least one PSK entry.
  const pskRange = asObject(obj["pskAppendRange"]!, `${what}.pskAppendRange`);
  closedKeys(pskRange, ["firstIndex", "lastIndex", "count"], `${what}.pskAppendRange`);
  const pskFirst = asPositiveInt(pskRange["firstIndex"], `${what}.pskAppendRange.firstIndex`);
  const pskLast = asPositiveInt(pskRange["lastIndex"], `${what}.pskAppendRange.lastIndex`);
  const pskCount = asPositiveInt(pskRange["count"], `${what}.pskAppendRange.count`);
  if (pskLast < pskFirst || pskLast - pskFirst + 1 !== pskCount)
    fail(`${what}.pskAppendRange: last-first+1 must equal count`);

  return {
    accepted: true,
    groupKind,
    basisRevision: asNonNegativeInt(obj["basisRevision"], `${what}.basisRevision`),
    basisSemanticEventCursor: asNonNegativeInt(obj["basisSemanticEventCursor"], `${what}.basisSemanticEventCursor`),
    resultRevision: asNonNegativeInt(obj["resultRevision"], `${what}.resultRevision`),
    resultSemanticEventCursor: asNonNegativeInt(obj["resultSemanticEventCursor"], `${what}.resultSemanticEventCursor`),
    actor,
    actionId,
    actionKind,
    capturedCount,
    settlementSteps,
    scored,
    terminalReason,
  };
}

function validateErrorBody(value: RestrictedValue, what: string): CommandErrorBody {
  const obj = asObject(value, what);
  closedKeys(obj, [
    "schemaVersion", "canonicalizationProfile", "commandId", "ruleset",
    "category", "code", "message",
    "authoritativeRevision", "authoritativeSemanticEventCursor", "retryDisposition",
  ], what);
  if (asString(obj["schemaVersion"], `${what}.schemaVersion`) !== ERROR_SCHEMA)
    fail(`${what}.schemaVersion is not collapse-error-v1`);
  requireProfile(obj["canonicalizationProfile"], `${what}.canonicalizationProfile`);
  const commandId = asString(obj["commandId"], `${what}.commandId`);
  if (commandId.length > 128 || !ID_GRAMMAR.test(commandId))
    fail(`${what}.commandId violates the bounded ASCII identifier grammar`);
  return {
    // Registered value domains (schemas/production/collapse-error-v1.schema.json).
    category: requireEnum(obj["category"], ERROR_CATEGORIES, `${what}.category`),
    code: requireEnum(obj["code"], ERROR_CODES, `${what}.code`),
    message: asString(obj["message"], `${what}.message`),
    retryDisposition: requireEnum(obj["retryDisposition"], RETRY_DISPOSITIONS, `${what}.retryDisposition`),
    authoritativeRevision: asNonNegativeInt(obj["authoritativeRevision"], `${what}.authoritativeRevision`),
    authoritativeSemanticEventCursor: asNonNegativeInt(obj["authoritativeSemanticEventCursor"], `${what}.authoritativeSemanticEventCursor`),
  };
}

export function validateCommandResult(value: RestrictedValue, what: string): CommandResultBody {
  const obj = asObject(value, what);
  const schemaVersion = asString(obj["schemaVersion"], `${what}.schemaVersion`);
  if (schemaVersion === ERROR_SCHEMA) {
    // Bare candidate error: the REJECTED journal record body.
    const error = validateErrorBody(value, what);
    const ruleset = validateRuleset(obj["ruleset"], `${what}.ruleset`);
    return {
      commandId: errorCommandId(value, what),
      accepted: false,
      ruleset,
      exactStateSha256: null,
      eventGroup: null,
      nextState: null,
      error,
    };
  }
  if (schemaVersion !== COMMAND_RESULT_SCHEMA)
    fail(`${what}.schemaVersion is neither a command result nor an error object`);
  closedKeys(obj, [
    "schemaVersion", "canonicalizationProfile", "commandId", "ruleset",
    "accepted", "eventGroup", "nextState", "exactStateSha256", "error",
  ], what);
  requireProfile(obj["canonicalizationProfile"], `${what}.canonicalizationProfile`);
  const ruleset = validateRuleset(obj["ruleset"], `${what}.ruleset`);
  // A rejected command result never enters the journal; this shape is
  // ACCEPTED-only (collapseprotocol.cpp assembleJournalRecord).
  if (asBool(obj["accepted"], `${what}.accepted`) !== true)
    fail(`${what}.accepted must be true on the command-result shape`);
  if (obj["error"] !== null) fail(`${what}.error must be null on an accepted result`);
  const commandId = asString(obj["commandId"], `${what}.commandId`);
  if (commandId.length > 128 || !ID_GRAMMAR.test(commandId))
    fail(`${what}.commandId violates the bounded ASCII identifier grammar`);
  const exactStateSha256 = requireHex64(obj["exactStateSha256"], `${what}.exactStateSha256`);
  const eventGroup = validateEventGroup(obj["eventGroup"]!, `${what}.eventGroup`);
  const nextState = validateExactState(obj["nextState"]!, `${what}.nextState`);
  // Cross-field bindings (eventGroupJson/commandResultJson): the group names
  // the same command, commits from the declared position, and carries the
  // same state digest as the envelope.
  const groupRaw = asObject(obj["eventGroup"]!, `${what}.eventGroup`);
  if (asString(groupRaw["commandId"], `${what}.eventGroup.commandId`) !== commandId)
    fail(`${what}.eventGroup.commandId does not match the result commandId`);
  if (requireHex64(groupRaw["resultExactStateSha256"], `${what}.eventGroup.resultExactStateSha256`) !== exactStateSha256)
    fail(`${what}.eventGroup.resultExactStateSha256 does not match the result digest`);
  if (eventGroup.resultRevision !== nextState.revision ||
      eventGroup.basisRevision !== nextState.revision - 1)
    fail(`${what}.eventGroup revisions do not bracket nextState.revision`);
  if (eventGroup.resultSemanticEventCursor !== nextState.semanticEventCursor)
    fail(`${what}.eventGroup.resultSemanticEventCursor does not match nextState`);
  return {
    commandId,
    accepted: true,
    ruleset,
    exactStateSha256,
    eventGroup,
    nextState,
    error: null,
  };
}

function errorCommandId(value: RestrictedValue, what: string): string {
  return asString(asObject(value, what)["commandId"], `${what}.commandId`);
}

export function validateSessionBound(value: RestrictedValue): SessionBoundPayload {
  const obj = asObject(value, "sessionBound payload");
  closedKeys(obj, ["revision", "created", "streamEpoch"], "sessionBound payload");
  const streamEpoch = asString(obj["streamEpoch"], "sessionBound.streamEpoch");
  // Stream epochs are 64-bit random hex tokens minted per session lifetime.
  if (!/^[0-9a-f]{16}$/.test(streamEpoch))
    fail("sessionBound.streamEpoch is not a 16-digit lowercase hex token");
  return {
    revision: asNonNegativeInt(obj["revision"], "sessionBound.revision"),
    created: asBool(obj["created"], "sessionBound.created"),
    streamEpoch,
  };
}

export function validateTransportErrorPayload(value: RestrictedValue): TransportErrorPayload {
  const obj = asObject(value, "transportError payload");
  closedKeys(obj, ["errorClass"], "transportError payload");
  return { errorClass: asString(obj["errorClass"], "transportError.errorClass") };
}

// ---------------------------------------------------------------------------
// Envelope parsing.
// ---------------------------------------------------------------------------

export function parseInboundEnvelope(text: string): InboundEnvelope {
  let value: RestrictedValue;
  try {
    value = parseRestrictedJson(text);
  } catch (error) {
    if (error instanceof RestrictedJsonError)
      fail(`inbound message is not restricted JSON (${error.code})`);
    throw error;
  }
  const obj = asObject(value, "inbound envelope");
  const schemaVersion = asString(obj["schemaVersion"], "envelope.schemaVersion");
  if (asInt(obj["envelopeVersion"], "envelope.envelopeVersion") !== ENVELOPE_VERSION)
    fail("envelope.envelopeVersion is not 1");
  const type = asString(obj["type"], "envelope.type");
  const payload = obj["payload"];
  if (payload === undefined) fail("envelope.payload is missing");

  if (schemaVersion === EVENT_SCHEMA) {
    closedKeys(obj, ["schemaVersion", "envelopeVersion", "sessionId", "streamSeq", "type", "payload"], "sessionEvent envelope");
    if (!(EVENT_TYPES as readonly string[]).includes(type)) fail(`unknown sessionEvent type: ${type}`);
    return {
      kind: "sessionEvent",
      event: {
        sessionId: asString(obj["sessionId"], "sessionEvent.sessionId"),
        streamSeq: asPositiveInt(obj["streamSeq"], "sessionEvent.streamSeq"),
        type: type as EventType,
        payload,
      },
    };
  }
  if (schemaVersion === CONNECTION_SCHEMA) {
    closedKeys(obj, ["schemaVersion", "envelopeVersion", "sessionId", "requestId", "type", "payload"], "connectionEvent envelope");
    if (!(CONNECTION_TYPES as readonly string[]).includes(type)) fail(`unknown connectionEvent type: ${type}`);
    const sessionId = obj["sessionId"];
    const requestId = obj["requestId"];
    return {
      kind: "connectionEvent",
      event: {
        sessionId: sessionId === null ? null : asString(sessionId, "connectionEvent.sessionId"),
        requestId: requestId === null ? null : asString(requestId, "connectionEvent.requestId"),
        type: type as ConnectionType,
        payload,
      },
    };
  }
  fail(`unknown envelope schemaVersion: ${schemaVersion}`);
}

// ---------------------------------------------------------------------------
// Per-type sessionEvent payload validators. Each returns the projection the
// store consumes; every one checks the closed key set of what the gateway
// publishes (mirrors session.js publish sites) so a shape drift fails closed.
// ---------------------------------------------------------------------------

export interface SessionStatePayload { state: "live" | "recovering"; revision: number | null }
export interface SessionErrorPayload { errorClass: string; detail: string }
export interface CommandResultPayload {
  commandId: string;
  result: CommandResultBody;
  redelivered: boolean;
}
export interface SnapshotPayload { state: ExactState; exactStateSha256: string }
export interface LegalMaskRange { first: number; last: number }
export interface LegalMaskPayload {
  revision: number;
  semanticEventCursor: number;
  exactStateSha256: string;
  legalActionCount: number;
  legalActionRanges: LegalMaskRange[];
}
export interface ExportLogPayload { format: string; sgf: string }
export interface PongPayload { revision: number; semanticEventCursor: number }
export interface ActionRef { actionId: number; kind: string }
export interface AnalysisRow {
  action: ActionRef;
  visits: number;
  qActorMicro: number;
  pv: ActionRef[];
}
export interface AnalysisUpdatePayload {
  basisRevision: number;
  basisSemanticEventCursor: number;
  chosenAction: ActionRef | null;
  elapsedMillis: number;
  moves: AnalysisRow[];
  playoutsCompleted: number;
}
export interface AnalysisErrorPayload { errorClass: string }

export type SessionEventPayload =
  | { type: "sessionState"; payload: SessionStatePayload }
  | { type: "sessionError"; payload: SessionErrorPayload }
  | { type: "commandResult"; payload: CommandResultPayload }
  | { type: "snapshot"; payload: SnapshotPayload }
  | { type: "legalMaskRead"; payload: LegalMaskPayload }
  | { type: "exportLog"; payload: ExportLogPayload }
  | { type: "pong"; payload: PongPayload }
  | { type: "analysisUpdate"; payload: AnalysisUpdatePayload }
  | { type: "analysisError"; payload: AnalysisErrorPayload };

function requireProfile(value: RestrictedValue | undefined, what: string): void {
  if (asString(value, what) !== CANONICALIZATION_PROFILE)
    fail(`${what} is not the restricted JSON profile`);
}

function requireHex64(value: RestrictedValue | undefined, what: string): string {
  const s = asString(value, what);
  if (!/^[0-9a-f]{64}$/.test(s)) fail(`${what} must be 64 lowercase hex characters`);
  return s;
}

export function validateActionV1(value: RestrictedValue, what: string): ActionRef {
  const obj = asObject(value, what);
  closedKeys(obj, ["schemaVersion", "actionId", "kind"], what);
  if (asString(obj["schemaVersion"], `${what}.schemaVersion`) !== ACTION_SCHEMA)
    fail(`${what}.schemaVersion is not action-v1`);
  const actionId = asInt(obj["actionId"], `${what}.actionId`);
  if (actionId < 0 || actionId >= FLAT_ACTION_COUNT)
    fail(`${what}.actionId is outside 0..${FLAT_ACTION_COUNT - 1}`);
  const kind = asString(obj["kind"], `${what}.kind`);
  // kind-major pairing (gameaction.cpp ofJson): PASS ⟺ 1444, otherwise the
  // kind's 361-wide plane.
  if (kind === "PASS") {
    if (actionId !== PASS_ACTION_ID) fail(`${what}: PASS must carry actionId 1444`);
  } else {
    const plane = { NORMAL: 0, IMMORTAL: 1, DOUBLE_START: 2, EIGHTWAY: 3 }[kind as ActionKind] as number | undefined;
    if (plane === undefined) fail(`${what}.kind is unknown`);
    if (actionId < plane * 361 || actionId > plane * 361 + 360)
      fail(`${what}.actionId is outside its kind plane`);
  }
  return { actionId, kind };
}

export function validateSessionStatePayload(value: RestrictedValue): SessionStatePayload {
  const obj = asObject(value, "sessionState payload");
  const state = asString(obj["state"], "sessionState.state");
  if (state === "live") {
    closedKeys(obj, ["state", "revision"], "sessionState payload");
    return { state, revision: asNonNegativeInt(obj["revision"], "sessionState.revision") };
  }
  if (state === "recovering") {
    closedKeys(obj, ["state"], "sessionState payload");
    return { state, revision: null };
  }
  return fail(`sessionState.state is unknown: ${state}`);
}

export function validateSessionErrorPayload(value: RestrictedValue): SessionErrorPayload {
  const obj = asObject(value, "sessionError payload");
  closedKeys(obj, ["errorClass", "detail"], "sessionError payload");
  return {
    errorClass: asString(obj["errorClass"], "sessionError.errorClass"),
    detail: asString(obj["detail"], "sessionError.detail"),
  };
}

export function validateCommandResultPayload(value: RestrictedValue): CommandResultPayload {
  const obj = asObject(value, "commandResult payload");
  closedKeys(obj, ["commandId", "result", "redelivered"], "commandResult payload");
  const commandId = asString(obj["commandId"], "commandResult.commandId");
  const result = validateCommandResult(obj["result"]!, "commandResult.result");
  if (result.commandId !== commandId)
    fail("commandResult.commandId does not match the embedded result commandId");
  return { commandId, result, redelivered: asBool(obj["redelivered"], "commandResult.redelivered") };
}

export function validateSnapshotPayload(value: RestrictedValue): SnapshotPayload {
  const obj = asObject(value, "snapshot payload");
  closedKeys(obj, ["snapshot"], "snapshot payload");
  const snap = asObject(obj["snapshot"]!, "snapshot");
  closedKeys(snap, [
    "canonicalizationProfile", "descriptor", "exactStateSha256",
    "integrity", "ruleset", "schemaVersion", "state",
  ], "snapshot");
  if (asString(snap["schemaVersion"], "snapshot.schemaVersion") !== SNAPSHOT_SCHEMA)
    fail("snapshot.schemaVersion is not collapse-snapshot-runtime-v1");
  requireProfile(snap["canonicalizationProfile"], "snapshot.canonicalizationProfile");
  validateRuleset(snap["ruleset"], "snapshot.ruleset");
  // descriptor is the gateway's digest-bound material (the client carries no
  // hashing): presence-checked. integrity is shape-checked closed — the
  // client never recomputes it, but a drifted shape fails closed.
  asObject(snap["descriptor"]!, "snapshot.descriptor");
  const integrity = asObject(snap["integrity"]!, "snapshot.integrity");
  closedKeys(integrity, [
    "algorithm", "digestSha256", "domain", "journalPrefixBytes",
    "journalPrefixSha256", "journalRecordOrdinal", "purpose",
  ], "snapshot.integrity");
  if (asString(integrity["algorithm"], "snapshot.integrity.algorithm") !== "SHA-256")
    fail("snapshot.integrity.algorithm must be SHA-256");
  if (asString(integrity["domain"], "snapshot.integrity.domain") !== "mutago.collapse-go.snapshot-prefix-v1")
    fail("snapshot.integrity.domain mismatch");
  if (asString(integrity["purpose"], "snapshot.integrity.purpose") !== "CORRUPTION_DETECTION_ONLY_NOT_AUTHENTICATION_OR_AUTHORITY")
    fail("snapshot.integrity.purpose mismatch");
  requireHex64(integrity["journalPrefixSha256"], "snapshot.integrity.journalPrefixSha256");
  requireHex64(integrity["digestSha256"], "snapshot.integrity.digestSha256");
  asNonNegativeInt(integrity["journalPrefixBytes"], "snapshot.integrity.journalPrefixBytes");
  asNonNegativeInt(integrity["journalRecordOrdinal"], "snapshot.integrity.journalRecordOrdinal");
  return {
    state: validateExactState(snap["state"]!, "snapshot.state"),
    exactStateSha256: requireHex64(snap["exactStateSha256"], "snapshot.exactStateSha256"),
  };
}

export function validateLegalMaskPayload(value: RestrictedValue): LegalMaskPayload {
  const obj = asObject(value, "legalMaskRead payload");
  closedKeys(obj, ["legalMask"], "legalMaskRead payload");
  const mask = asObject(obj["legalMask"]!, "legalMask");
  closedKeys(mask, [
    "actionCodecVersion", "canonicalizationProfile", "exactStateSha256", "flatActionCount",
    "legalActionCount", "legalActionRanges", "revision", "ruleset", "schemaVersion",
    "semanticEventCursor",
  ], "legalMask");
  if (asString(mask["schemaVersion"], "legalMask.schemaVersion") !== LEGAL_MASK_SCHEMA)
    fail("legalMask.schemaVersion is not collapse-legal-mask-read-runtime-v1");
  requireProfile(mask["canonicalizationProfile"], "legalMask.canonicalizationProfile");
  validateRuleset(mask["ruleset"], "legalMask.ruleset");
  if (asInt(mask["actionCodecVersion"], "legalMask.actionCodecVersion") !== ACTION_CODEC_VERSION)
    fail("legalMask.actionCodecVersion is not 1");
  if (asInt(mask["flatActionCount"], "legalMask.flatActionCount") !== FLAT_ACTION_COUNT)
    fail(`legalMask.flatActionCount is not ${FLAT_ACTION_COUNT}`);
  const legalActionCount = asInt(mask["legalActionCount"], "legalMask.legalActionCount");
  if (legalActionCount < 0 || legalActionCount > FLAT_ACTION_COUNT)
    fail("legalMask.legalActionCount is outside the flat action space");
  const rangesValue = mask["legalActionRanges"];
  if (!Array.isArray(rangesValue)) fail("legalMask.legalActionRanges must be an array");
  if (rangesValue.length > FLAT_ACTION_COUNT)
    fail("legalMask.legalActionRanges exceeds the entry cap");
  let covered = 0;
  let previousLast: number | null = null;
  const legalActionRanges = rangesValue.map((range, i) => {
    const r = asObject(range, `legalMask.legalActionRanges[${i}]`);
    closedKeys(r, ["first", "last"], `legalMask.legalActionRanges[${i}]`);
    const first = asInt(r["first"], `legalMask.legalActionRanges[${i}].first`);
    const last = asInt(r["last"], `legalMask.legalActionRanges[${i}].last`);
    if (first < 0 || first > last || last >= FLAT_ACTION_COUNT)
      fail(`legalMask.legalActionRanges[${i}] is out of bounds`);
    // The C++ emitter produces strictly ascending maximal runs: overlapping
    // or adjacent-but-unmerged ranges contradict the count on display.
    if (previousLast !== null && first <= previousLast + 1)
      fail(`legalMask.legalActionRanges[${i}] overlaps or is adjacent to the previous range`);
    previousLast = last;
    covered += last - first + 1;
    return { first, last };
  });
  // The count must equal the ranges' coverage: a contradictory projection
  // (zero legal actions but hundreds of highlighted points) fails closed.
  if (covered !== legalActionCount)
    fail("legalMask.legalActionCount does not equal the ranges' coverage");
  return {
    revision: asNonNegativeInt(mask["revision"], "legalMask.revision"),
    semanticEventCursor: asNonNegativeInt(mask["semanticEventCursor"], "legalMask.semanticEventCursor"),
    exactStateSha256: requireHex64(mask["exactStateSha256"], "legalMask.exactStateSha256"),
    legalActionCount,
    legalActionRanges,
  };
}

export function validateExportLogPayload(value: RestrictedValue): ExportLogPayload {
  const obj = asObject(value, "exportLog payload");
  closedKeys(obj, ["format", "sgf"], "exportLog payload");
  if (asString(obj["format"], "exportLog.format") !== EXPORT_FORMAT)
    fail("exportLog.format is not collapse-sgf-loss-v1");
  return { format: EXPORT_FORMAT, sgf: asString(obj["sgf"], "exportLog.sgf") };
}

export function validatePongPayload(value: RestrictedValue): PongPayload {
  const obj = asObject(value, "pong payload");
  closedKeys(obj, ["revision", "semanticEventCursor"], "pong payload");
  return {
    revision: asNonNegativeInt(obj["revision"], "pong.revision"),
    semanticEventCursor: asNonNegativeInt(obj["semanticEventCursor"], "pong.semanticEventCursor"),
  };
}

export function validateAnalysisUpdatePayload(value: RestrictedValue): AnalysisUpdatePayload {
  const obj = asObject(value, "analysisUpdate payload");
  closedKeys(obj, [
    "authoritative", "basisRevision", "basisSemanticEventCursor", "chosenAction",
    "elapsedMillis", "moves", "playoutsCompleted",
  ], "analysisUpdate payload");
  if (asBool(obj["authoritative"], "analysisUpdate.authoritative") !== false)
    fail("analysisUpdate.authoritative must be false");
  const chosenRaw = obj["chosenAction"];
  const movesValue = obj["moves"];
  if (!Array.isArray(movesValue)) fail("analysisUpdate.moves must be an array");
  if (movesValue.length > ANALYSIS_MAX_ROWS)
    fail("analysisUpdate.moves exceeds the row cap");
  const moves = movesValue.map((row, i) => {
    const r = asObject(row, `analysisUpdate.moves[${i}]`);
    closedKeys(r, ["action", "pv", "qActorMicro", "visits"], `analysisUpdate.moves[${i}]`);
    const qActorMicro = asInt(r["qActorMicro"], `analysisUpdate.moves[${i}].qActorMicro`);
    if (qActorMicro < -1000000 || qActorMicro > 1000000)
      fail(`analysisUpdate.moves[${i}].qActorMicro is out of range`);
    const pvValue = r["pv"];
    if (!Array.isArray(pvValue)) fail(`analysisUpdate.moves[${i}].pv must be an array`);
    if (pvValue.length > ANALYSIS_PV_DEPTH)
      fail(`analysisUpdate.moves[${i}].pv exceeds the depth cap`);
    return {
      action: validateActionV1(r["action"]!, `analysisUpdate.moves[${i}].action`),
      visits: asNonNegativeInt(r["visits"], `analysisUpdate.moves[${i}].visits`),
      qActorMicro,
      pv: pvValue.map((a, j) => validateActionV1(a, `analysisUpdate.moves[${i}].pv[${j}]`)),
    };
  });
  return {
    basisRevision: asNonNegativeInt(obj["basisRevision"], "analysisUpdate.basisRevision"),
    basisSemanticEventCursor: asNonNegativeInt(obj["basisSemanticEventCursor"], "analysisUpdate.basisSemanticEventCursor"),
    chosenAction: chosenRaw === null
      ? null
      : validateActionV1(chosenRaw!, "analysisUpdate.chosenAction"),
    elapsedMillis: asNonNegativeInt(obj["elapsedMillis"], "analysisUpdate.elapsedMillis"),
    moves,
    playoutsCompleted: asNonNegativeInt(obj["playoutsCompleted"], "analysisUpdate.playoutsCompleted"),
  };
}

export function validateAnalysisErrorPayload(value: RestrictedValue): AnalysisErrorPayload {
  const obj = asObject(value, "analysisError payload");
  closedKeys(obj, ["errorClass"], "analysisError payload");
  return { errorClass: asString(obj["errorClass"], "analysisError.errorClass") };
}

// Dispatches a sessionEvent payload to its per-type validator.
export function validateSessionEventPayload(
  type: EventType,
  payload: RestrictedValue,
): SessionEventPayload {
  switch (type) {
    case "sessionState": return { type, payload: validateSessionStatePayload(payload) };
    case "sessionError": return { type, payload: validateSessionErrorPayload(payload) };
    case "commandResult": return { type, payload: validateCommandResultPayload(payload) };
    case "snapshot": return { type, payload: validateSnapshotPayload(payload) };
    case "legalMaskRead": return { type, payload: validateLegalMaskPayload(payload) };
    case "exportLog": return { type, payload: validateExportLogPayload(payload) };
    case "pong": return { type, payload: validatePongPayload(payload) };
    case "analysisUpdate": return { type, payload: validateAnalysisUpdatePayload(payload) };
    case "analysisError": return { type, payload: validateAnalysisErrorPayload(payload) };
    default: return fail(`no payload validator for sessionEvent type: ${type}`);
  }
}
